import { Expression, LinePaint, SymbolLayout, SymbolPaint } from "mapbox-gl";
import { Feature, LineString, MultiLineString } from "geojson";
import { Layer, Source } from "react-map-gl";

import { RobotSummaryResponse } from "protos/portal/robots";
import React, { FC } from "react";

interface TractorHistoryLayersProps {
  drivePathMapData: DrivePathMapDataEntry;
}

// GeoJSON feature property keys.
export enum Property {
  SERIAL = "serial",
  LINE_WIDTH_METERS = "lineWidthMeters",
  LATITUDE_RADIANS = "latitudeRadians",
}
export interface DrivePathMapDataEntry {
  serial: string;
  robot: RobotSummaryResponse;
  combinedPath: Feature<LineString>;
  activePath: Feature<MultiLineString>;
  inactivePath: Feature<MultiLineString>;
}
export const TractorHistoryLayers: FC<TractorHistoryLayersProps> = ({
  drivePathMapData,
}) => {
  const { activePath, inactivePath, combinedPath, serial } = drivePathMapData;
  return (
    <>
      <Source
        id={`active-${serial}`}
        type="geojson"
        lineMetrics
        data={activePath}
      >
        <Layer id={`active-${serial}`} type="line" paint={activePaint} />
      </Source>
      <Source
        id={`inactive-${serial}`}
        type="geojson"
        lineMetrics
        data={inactivePath}
      >
        <Layer id={`inactive-${serial}`} type="line" paint={inactivePaint} />
      </Source>
      <Source
        id={`combined-${serial}`}
        type="geojson"
        lineMetrics
        data={combinedPath}
      >
        <Layer id={`circle-${serial}`} type="circle" paint={circlePaint} />
        <Layer id={`path-${serial}`} type="line" paint={linePaint} />
        <Layer
          id={`path-arrows-${serial}`}
          type="symbol"
          layout={arrowLayout}
          paint={arrowPaint}
        />
      </Source>
    </>
  );
};

const makeImplementPaint = (color: string): LinePaint => {
  // https://github.com/mapbox/mapbox-gl-js/issues/5861#issuecomment-2654740939
  // eslint-disable-next-line unicorn/consistent-function-scoping
  const pxPerMeterAtZoom = (zoomLevel: number): Expression => {
    // At zoom level 16, one pixel at the equator corresponds to 1.194 meters:
    // https://docs.mapbox.com/help/glossary/zoom-level/
    const baseZoom = 16;
    const baseWidthAtEquator = 1 / 1.194; // px/meter
    // Each zoom level magnifies by a factor of two.
    const zoomScale = 2 ** (zoomLevel - baseZoom);
    const zoomedWidthAtEquator = baseWidthAtEquator * zoomScale;
    // And, away from the equator, there are more pixels per meter.
    const latitudeRadians = ["get", Property.LATITUDE_RADIANS];
    const projectionScale = ["/", 1, ["cos", latitudeRadians]];
    return ["*", zoomedWidthAtEquator, projectionScale];
  };

  const zoomStop = (
    zoomLevel: number
  ): [zoomLevel: number, lineWidth: Expression] => {
    const lineWidthMeters = ["get", Property.LINE_WIDTH_METERS];
    return [zoomLevel, ["*", lineWidthMeters, pxPerMeterAtZoom(zoomLevel)]];
  };

  return {
    // Use line-gradient instead of line-color so that overlaps regions get
    // rendered with more opacity.
    "line-gradient": ["string", color], // appease @types/mapbox-gl
    "line-width": [
      "interpolate",
      ["exponential", 2],
      ["zoom"],
      ...zoomStop(10),
      ...zoomStop(24),
    ],
  };
};

const activePaint = makeImplementPaint("rgba(29, 161, 242, 0.6)");
const inactivePaint = makeImplementPaint("rgba(224, 224, 224, 0.3)");

const linePaint = {
  "line-color": "black",
  "line-width": 1,
} as const;
// Direction arrows along the path, matching transport task centerlines.
const arrowLayout: SymbolLayout = {
  "symbol-placement": "line",
  "symbol-spacing": 100,
  "text-field": "▶",
  "text-size": 40,
  "text-keep-upright": false,
  "text-rotation-alignment": "map",
};
const arrowPaint: SymbolPaint = {
  "text-color": "black",
  "text-opacity": 0.8,
};
const circlePaint = {
  "circle-color": "black",
  "circle-radius": 2,
} as const;
